import React, { FC, useState, useEffect, useRef, MouseEvent } from 'react';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { documentService } from '../../../utils/window.utils';
import Icon from '../icon';
import PortalComponent from './components/portalComponent';
import { TooltipProps } from './types/infoToolTip.types';

const InfoToolTip: FC<TooltipProps> = ({
  children,
  showIcon,
  clickPreventDefault = false,
  width = 300,
  color = 'complement',
  className = '',
}) => {
  const TooltipIconRef = useRef<HTMLElement | null>(null);
  const [tooltipVisibility, setTooltipVisibility] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const customDocument = documentService();
  let tooltipDiv;
  if (customDocument && customDocument?.createElement) {
    tooltipDiv = customDocument?.createElement('div');
  }

  useEffect(() => {
    customDocument?.body.appendChild(tooltipDiv);
    return (): void => {
      customDocument?.body.removeChild(tooltipDiv);
    };
  }, [tooltipDiv]); //eslint-disable-line

  useEffect(() => {
    function handleClickEvent(e): void {
      if (isMobile && TooltipIconRef && TooltipIconRef.current && !TooltipIconRef.current.contains(e?.target)) {
        setTooltipVisibility(false);
      }
    }

    customDocument?.addEventListener('click', handleClickEvent);
    return (): void => customDocument?.removeEventListener('click', handleClickEvent);
  }, [isMobile, TooltipIconRef, customDocument]); //eslint-disable-line

  const showTooltip = (): void => {
    setTooltipVisibility(true);
  };

  const hideTooltip = (): void => {
    setTooltipVisibility(false);
  };

  const handleMouseOver = (): void => {
    if (!isMobile) {
      showTooltip();
    }
  };

  const handleMouseOut = (): void => {
    if (!isMobile) {
      hideTooltip();
    }
  };

  const handleClick = (e: MouseEvent): void => {
    if (isMobile) {
      showTooltip();
    }
    if (clickPreventDefault) {
      e.preventDefault();
    }
  };

  const handleBlur = (): void => {
    if (isMobile) {
      hideTooltip();
    }
  };

  const getTooltipIconDimensions = (): DOMRect | null => {
    if (TooltipIconRef && TooltipIconRef.current) {
      return TooltipIconRef.current.getBoundingClientRect();
    }
    return null;
  };

  if (!showIcon) return null;

  return (
    <div className={`info-tooltip ${className}`}>
      <div
        className="info-tooltip__icon"
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={handleClick}
        onBlur={handleBlur}
      >
        {showIcon && <Icon ref={TooltipIconRef} iconName="info-alert-notif-info" size={18} color={color} />}
      </div>
      <PortalComponent
        tooltipDiv={tooltipDiv}
        getTooltipIconDimensions={getTooltipIconDimensions}
        width={width}
        tooltipVisibility={tooltipVisibility}
      >
        {children}
      </PortalComponent>
    </div>
  );
};

export default InfoToolTip;
